import i18n from '@/i18n/config';
import { runtimeConfig } from '@/config/runtime';
import { type ExtraSigninRequestArgs, UserManager, WebStorageStateStore } from 'oidc-client-ts';

export const userManager = new UserManager({
  authority: runtimeConfig.authority,
  // biome-ignore lint/style/useNamingConvention: oidc-client-ts option names
  client_id: runtimeConfig.clientId,
  /*
   * Keycloak returns the browser to the exact page it left from, so a deep link such as a shared
   * review URL survives the round trip without parking it anywhere. The client registration has
   * to allow the whole origin (`http://host/*`) for this to hold.
   */
  // biome-ignore lint/style/useNamingConvention: oidc-client-ts option names
  redirect_uri: `${window.location.origin}${window.location.pathname}`,
  /*
   * The trailing slash matters: Keycloak validates this against the same registered patterns as
   * the redirect URI, and a bare origin does not match `<origin>/*`, so signing out would end on
   * "Invalid redirect uri" instead of back at the application.
   */
  // biome-ignore lint/style/useNamingConvention: oidc-client-ts option names
  post_logout_redirect_uri: `${window.location.origin}/`,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  revokeTokenTypes: ['refresh_token'],
  automaticSilentRenew: true,
  // Cross-tab login/logout detection.
  monitorSession: true,
  loadUserInfo: true,
});

/**
 * The interface language as an OIDC `ui_locales` value.
 *
 * It carries the language to Keycloak, so its pages open in the same language as the application
 * rather than in whatever the browser asks for.
 */
const uiLocale = (): string => i18n.language.split('-')[0];

/** Arguments for every sign-in redirect. */
export const signinArgs = (): ExtraSigninRequestArgs => ({
  // biome-ignore lint/style/useNamingConvention: OIDC parameter name
  ui_locales: uiLocale(),
});

/** Arguments for the sign-out redirect, which takes the same parameter one level deeper. */
export const signoutArgs = (): { extraQueryParams: Record<string, string> } => ({
  // biome-ignore lint/style/useNamingConvention: OIDC parameter name
  extraQueryParams: { ui_locales: uiLocale() },
});

/** Strip the `code`/`state` query from the URL once the library has consumed them. */
export const onSigninCallback = (): void => {
  window.history.replaceState({}, document.title, window.location.pathname);
};
